import { createElement } from '../../../core/dom/helpers.js';
import { extractVideoId, buildEmbedUrl } from '../../../core/media/youtube-url.js';
import { createVideoCard } from './video-card.js';

/**
 * @typedef {import('../../../contracts/forms.js').VideoEntry} VideoEntry
 */

/** Monotonic client-key source for video cards (keys are never reused). */
let cardKeySeq = 0;
function nextCardKey() { return `vc-${++cardKeySeq}`; }

/**
 * Derive `youtube_id`/`youtube_link` for an entry from its raw URL. The URL is the single
 * source of truth: a resolvable id canonicalizes the link to the embed form; otherwise the
 * id is '' and the link is kept verbatim (so an invalid-but-non-blank row blocks save and the
 * user sees what they typed). Title/description pass through.
 * @param {VideoEntry} entry
 * @returns {VideoEntry}
 */
function deriveEntry(entry) {
  const rawUrl = entry.youtube_link ?? '';
  const id = extractVideoId(rawUrl) ?? '';
  return {
    youtube_id: id,
    youtube_link: id ? buildEmbedUrl(id) : rawUrl,
    title: entry.title ?? '',
    description: entry.description ?? '',
  };
}

/** Normalize an arbitrary incoming value (ctx.value / setValue) into a VideoEntry[]. @param {unknown} value */
function toEntries(value) {
  if (!Array.isArray(value)) return [];
  return value.map((v) => {
    const o = /** @type {Record<string, unknown>} */ (v && typeof v === 'object' ? v : {});
    return deriveEntry({
      youtube_id: typeof o.youtube_id === 'string' ? o.youtube_id : '',
      youtube_link: typeof o.youtube_link === 'string' ? o.youtube_link : '',
      title: typeof o.title === 'string' ? o.title : '',
      description: typeof o.description === 'string' ? o.description : '',
    });
  });
}

/**
 * Videos driver — an ordered list of YouTube video cards with add / remove / drag-reorder.
 * Leaner sibling of the file-driver multi gallery (no upload, no network, no async session).
 *
 * @type {import('../../../contracts/forms.js').FieldDriver}
 */
export const videosDriver = {
  type: 'videos',
  /**
   * @param {HTMLElement} container
   * @param {import('../../../contracts/forms.js').FieldDriverContext} ctx
   * @returns {import('../../../contracts/forms.js').FieldDriverHandle}
   */
  mount(container, ctx) {
    const { onChange } = ctx;
    const def = /** @type {import('../../../contracts/forms.js').VideoFieldDef} */ (ctx.def);

    /** @type {VideoEntry[]} */
    let currentValues = toEntries(ctx.value);
    /** Stable client keys, lockstep with currentValues. */
    let keys = currentValues.map(() => nextCardKey());
    /** Live card handles for the current render — destroyed before each rebuild. */
    /** @type {ReturnType<typeof createVideoCard>[]} */
    let cardHandles = [];
    let disabled = Boolean(def.disabled);

    const wrapper = createElement('div', { class: 'videos-driver' });
    const grid = createElement('div', { class: 'videos-driver__grid' });

    const addBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
      type: 'button', class: 'videos-driver__add btn btn-primary',
    }, ['Add video']));
    addBtn.disabled = disabled; // honor def.disabled at mount (cards are disabled in renderAll)

    wrapper.appendChild(grid);
    wrapper.appendChild(addBtn);
    container.appendChild(wrapper);

    /** Emit a fresh snapshot array (reference-stable: only on real change). */
    function emit() { onChange(currentValues.map((v) => ({ ...v }))); }

    /** @type {string | null} */
    let draggedKey = null;

    /** Patch the entry at the index resolved from `key`, re-deriving id/link. @param {string} key @param {Partial<VideoEntry>} patch */
    function patchByKey(key, patch) {
      const i = keys.indexOf(key);
      if (i === -1) return;
      currentValues = currentValues.map((v, j) => (j === i ? deriveEntry({ ...v, ...patch }) : v));
      emit();
    }

    function addEntry() {
      currentValues = [...currentValues, deriveEntry({ youtube_id: '', youtube_link: '', title: '', description: '' })];
      keys = [...keys, nextCardKey()];
      renderAll();
      emit();
    }

    /** @param {number} i */
    function removeAt(i) {
      if (i < 0 || i >= currentValues.length) return;
      currentValues = [...currentValues.slice(0, i), ...currentValues.slice(i + 1)];
      keys = [...keys.slice(0, i), ...keys.slice(i + 1)];
      renderAll();
      emit();
    }

    /**
     * @param {number} fromIdx @param {number} toIdx @param {{ focus?: boolean }} [opts]
     * `opts.focus` (set ONLY by the Move-button callbacks, never by drag/drop) re-homes
     * keyboard focus onto the moved card's Move button after the re-render — a drag must
     * NOT steal focus to a button.
     */
    function moveItem(fromIdx, toIdx, opts = {}) {
      if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0 || fromIdx >= currentValues.length || toIdx >= currentValues.length) return;
      const movedKey = keys[fromIdx];
      const movingUp = toIdx < fromIdx;
      const nv = [...currentValues]; const nk = [...keys];
      const [mv] = nv.splice(fromIdx, 1); const [mk] = nk.splice(fromIdx, 1);
      nv.splice(toIdx, 0, mv); nk.splice(toIdx, 0, mk);
      currentValues = nv; keys = nk;
      renderAll();
      emit();
      if (opts.focus) {
        const handle = cardHandles[keys.indexOf(movedKey)];
        handle?.focusMove?.(movingUp);
      }
    }

    /** Build the card at index `idx` with drag SPLIT (handle = origin, root = drop target). @param {number} idx */
    function makeCard(idx) {
      const card = createVideoCard({
        key: keys[idx],
        value: currentValues[idx],
        onUrlChange: (key, url) => patchByKey(key, { youtube_link: url }),
        onTitleChange: (key, text) => patchByKey(key, { title: text }),
        onDescriptionChange: (key, text) => patchByKey(key, { description: text }),
        onRemove: (key) => {
          if (disabled) return;
          removeAt(keys.indexOf(key));
        },
        onMoveUp: (key) => {
          if (disabled) return;
          const i = keys.indexOf(key);
          moveItem(i, i - 1, { focus: true });
        },
        onMoveDown: (key) => {
          if (disabled) return;
          const i = keys.indexOf(key);
          moveItem(i, i + 1, { focus: true });
        },
      });
      cardHandles.push(card);
      const root = card.el;
      root.dataset.cardIdx = String(idx);

      const handle = card.handle;
      handle.addEventListener('dragstart', (e) => {
        if (disabled) { e.preventDefault(); return; }
        draggedKey = keys[idx];
        root.classList.add('video-card--dragging');
        if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
      });
      handle.addEventListener('dragend', () => {
        draggedKey = null;
        root.classList.remove('video-card--dragging');
        grid.querySelectorAll('.video-card--over').forEach((el) => el.classList.remove('video-card--over'));
      });
      root.addEventListener('dragover', (e) => {
        if (draggedKey === null) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        root.classList.add('video-card--over');
      });
      root.addEventListener('dragleave', () => root.classList.remove('video-card--over'));
      root.addEventListener('drop', (e) => {
        e.preventDefault();
        root.classList.remove('video-card--over');
        if (draggedKey === null) return;
        moveItem(keys.indexOf(draggedKey), parseInt(root.dataset.cardIdx ?? '-1', 10));
      });
      return root;
    }

    function renderAll() {
      if (cardHandles.length) { for (const h of cardHandles) h.destroy(); cardHandles = []; }
      grid.textContent = '';
      currentValues.forEach((_v, i) => grid.appendChild(makeCard(i)));
      const last = cardHandles.length - 1;
      cardHandles.forEach((h, i) => {
        h.setMoveState(i > 0, i < last); // first can't move up; last can't move down
        h.setDisabled(disabled);
      });
    }

    addBtn.addEventListener('click', () => {
      if (disabled) return;
      addEntry();
    });

    renderAll();

    return {
      getValue() {
        // Re-derive from the LIVE state (the card inputs already drive currentValues via
        // onUrlChange/onTitleChange/etc.), so even a Save that skips a blur sees the
        // current URLs. deriveEntry re-runs id/link resolution defensively.
        return currentValues.map((v) => deriveEntry(v));
      },
      setValue(value) {
        currentValues = toEntries(value);
        keys = currentValues.map(() => nextCardKey());
        renderAll();
      },
      clearValue() {
        if (currentValues.length === 0) return; // idempotent, same reference; never emits
        currentValues = [];
        keys = [];
        renderAll();
      },
      setDisabled(d) {
        disabled = d;
        addBtn.disabled = d;
        for (const h of cardHandles) h.setDisabled(d);
      },
      setVisible(visible) {
        wrapper.hidden = !visible;
      },
      setErrors() {},
      setAriaInvalid(invalid) {
        wrapper.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      },
      destroy() {
        for (const h of cardHandles) h.destroy();
        cardHandles = [];
        wrapper.remove();
      },
    };
  },
};
